/**
 * Blocking pre-clipboard secret tripwire — the Copy-gate that stops
 * a user pasting a token-shaped string into a Slack thread.
 *
 * Distinct from the `redactSecrets` helper in
 * `packages/crucible-gui/src/shared/redact-secrets.ts`:
 *
 *   - `redactSecrets` REPLACES matches with `[REDACTED-...]` markers.
 *     Already runs on the rendered text before this scan.
 *   - `scanForSecrets` (here) RE-SCANS the redacted text and returns
 *     a flat list of remaining matches. Anything it finds is by
 *     definition NOT covered by the first-pass redactor — either a
 *     new pattern (npm token, Datadog hex key, etc.) OR a high-
 *     entropy fallback hit on a base64-like substring that didn't
 *     match a named pattern.
 *
 * The Copy gate keys off `flags.length === 0` — when non-zero, the
 * renderer surfaces the `<SecretFlagBanner />` + the global
 * "I've reviewed all flagged lines" checkbox; only after the user
 * ticks that AND no zero-byte clipboard-paste guard fires does the
 * Copy button enable.
 *
 * TDD §C-1 "Secret-scan tripwire" — the closed `SecretPatternLabel`
 * enum is locked here; new patterns require a TDD update.
 *
 * Dedupe rule: a line matched by a named pattern does NOT also fire
 * the `high-entropy` fallback for the same byte range. The flat
 * output is deduped by `(lineIndex, matchedRangeStart..matchedRangeEnd)`.
 */
const NAMED_PATTERNS = [
    // GitHub PATs — five prefix families, each ≥20 base62 chars. Word-
    // boundary anchored so a partial match doesn't false-positive on
    // a base64-like blob.
    { label: 'github-pat-ghp', regex: /\bghp_[A-Za-z0-9]{20,}/g },
    { label: 'github-pat-gho', regex: /\bgho_[A-Za-z0-9]{20,}/g },
    { label: 'github-pat-ghs', regex: /\bghs_[A-Za-z0-9]{20,}/g },
    { label: 'github-pat-ghu', regex: /\bghu_[A-Za-z0-9]{20,}/g },
    { label: 'github-pat-ghr', regex: /\bghr_[A-Za-z0-9]{20,}/g },
    // Fine-grained PATs use the `github_pat_` prefix. Underscores
    // allowed in the body (per GitHub's docs).
    { label: 'github-pat-fine-grained', regex: /\bgithub_pat_[A-Za-z0-9_]{20,}/g },
    // Anthropic API keys.
    { label: 'anthropic-key', regex: /sk-ant-[A-Za-z0-9_-]{20,}/g },
    // AWS access keys — AKIA (long-lived), ASIA (STS session),
    // AROA (role), AIDA (user). Always 16-char base32-ish body.
    { label: 'aws-key', regex: /\b(?:AKIA|ASIA|AROA|AIDA)[A-Z0-9]{16}\b/g },
    // AWS secret access keys — 40-char base64 incl. `+/=`. There is
    // no fixed prefix on the secret itself, so we anchor on the
    // common env-var / config-key shapes:
    //   AWS_SECRET_ACCESS_KEY = ...
    //   aws_secret_access_key = ...
    //   aws.secret.access.key=...
    //   "secretAccessKey": "..."
    // The high-entropy fallback excludes `/` (paths) so a raw 40-char
    // base64 secret would otherwise evade detection — see Codex round-2
    // HIGH on PR #1075. Case-insensitive. The optional `["']?` after
    // the key name AND before the assign operator handle JSON-style
    // shapes like `"secretAccessKey": "wJa...".`
    {
        label: 'aws-secret-key',
        regex: /\b(?:aws[_.\s-]*secret[_.\s-]*access[_.\s-]*key|secretAccessKey)\b["']?\s*[=:]\s*["']?[A-Za-z0-9+/=]{40}/gi,
    },
    // npm access tokens.
    { label: 'npm-token', regex: /\bnpm_[A-Za-z0-9]{20,}/g },
    // `Authorization: Bearer <token>` headers. Case-insensitive on
    // the header word so `bearer` / `BEARER` are caught too.
    {
        label: 'http-bearer',
        regex: /\bBearer\s+[A-Za-z0-9._\-+/=]{20,}/gi,
    },
    // Credentialed URLs — `https://user:pass@host`. The userinfo
    // chunk can include x-access-token:pat shapes.
    {
        label: 'credentialed-url',
        regex: /\bhttps?:\/\/[^@\s/]+@[^\s]+/gi,
    },
    // Datadog API key — 32 lowercase hex. Narrow enough not to false-
    // positive on the sha256-8 surrogate shape (which is only 8 hex
    // chars). \b boundaries so the 40-char Datadog app-key shape
    // (handled separately below) doesn't accidentally match this 32-
    // char one too.
    { label: 'datadog-api-key', regex: /\b[a-f0-9]{32}\b/g },
    // Datadog app key — 40 lowercase hex. \b boundary keeps a 64-char
    // SHA-256 from registering as two adjacent 32-char keys.
    { label: 'datadog-app-key', regex: /\b[a-f0-9]{40}\b/g },
];
/** High-entropy fallback: base64-like substrings ≥ 32 chars.
 *
 *  Excludes `/` from the character class so on-disk paths (which
 *  legitimately contain runs of 32+ characters of `[A-Za-z0-9_-]`
 *  separated by `/`) don't false-positive. Real tokens are typically
 *  base64url or hex — they don't have slashes — so the exclusion
 *  costs nothing in practice. Base64-with-padding (`=`) is still
 *  matched.
 *
 *  The TDD acknowledges some false-positive cost (long hash-like
 *  strings that ARE benign); the Copy gate's global "I've reviewed"
 *  checkbox is the operator's escape hatch. Missing a real secret
 *  is the worse outcome. */
const HIGH_ENTROPY_REGEX = /[A-Za-z0-9+=_-]{32,}/g;
/**
 * Scan rendered text for token-shaped substrings. Returns a flat
 * array of flags, deduped by (lineIndex, range). Empty array means
 * the Copy gate enables directly.
 *
 * Pure function — deterministic across runs, no I/O, no global
 * state. Tests pin against fixture strings.
 */
export function scanForSecrets(renderedText) {
    const flags = [];
    const lines = renderedText.split('\n');
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        if (line.length === 0)
            continue;
        // Track ranges covered by named patterns so the high-entropy
        // fallback can skip overlaps. Sorted+merged at the end for
        // O(n + named) lookup.
        const namedCoverage = [];
        for (const pattern of NAMED_PATTERNS) {
            // Reset lastIndex defensively — global regexes are sticky
            // across `.exec` calls.
            pattern.regex.lastIndex = 0;
            let match;
            while ((match = pattern.regex.exec(line)) !== null) {
                const start = match.index;
                const end = match.index + match[0].length;
                flags.push({
                    lineIndex,
                    patternLabel: pattern.label,
                    matchedRangeStart: start,
                    matchedRangeEnd: end,
                });
                namedCoverage.push([start, end]);
                // Defensive guard against zero-width matches (shouldn't
                // happen with the patterns above, but the regex engine's
                // behaviour on `/x*/g` is to advance by one to avoid an
                // infinite loop — we mirror that).
                if (match[0].length === 0)
                    pattern.regex.lastIndex++;
            }
        }
        // High-entropy fallback. Only fires on ranges NOT already
        // covered by a named pattern hit on this line.
        HIGH_ENTROPY_REGEX.lastIndex = 0;
        let entropyMatch;
        while ((entropyMatch = HIGH_ENTROPY_REGEX.exec(line)) !== null) {
            const start = entropyMatch.index;
            const end = entropyMatch.index + entropyMatch[0].length;
            if (rangeOverlapsAny(start, end, namedCoverage))
                continue;
            flags.push({
                lineIndex,
                patternLabel: 'high-entropy',
                matchedRangeStart: start,
                matchedRangeEnd: end,
            });
            if (entropyMatch[0].length === 0)
                HIGH_ENTROPY_REGEX.lastIndex++;
        }
    }
    // Final dedupe by (lineIndex, start, end). The named-pattern loop
    // can produce multiple labels for the SAME range when an Anthropic
    // key happens to also match the Datadog 32-hex shape (would not
    // happen in practice — sk-ant- prefix differs — but cheap to be
    // defensive). Stable-sort by line then by start so the order is
    // deterministic for the renderer's summary.
    flags.sort((a, b) => {
        if (a.lineIndex !== b.lineIndex)
            return a.lineIndex - b.lineIndex;
        if (a.matchedRangeStart !== b.matchedRangeStart) {
            return a.matchedRangeStart - b.matchedRangeStart;
        }
        return a.matchedRangeEnd - b.matchedRangeEnd;
    });
    const seen = new Set();
    const deduped = [];
    for (const flag of flags) {
        const key = `${flag.lineIndex}:${flag.matchedRangeStart}:${flag.matchedRangeEnd}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        deduped.push(flag);
    }
    return deduped;
}
/**
 * Summarise flags by pattern label for the
 * `<SecretFlagBanner />` summary chip — `2× github-pat-ghp,
 * 1× credentialed-url` style. Pure helper kept here so the renderer
 * doesn't have to re-derive the same shape. Sorted by descending
 * count then by label for a stable display order.
 */
export function summariseSecretFlags(flags) {
    const counts = new Map();
    for (const flag of flags) {
        counts.set(flag.patternLabel, (counts.get(flag.patternLabel) ?? 0) + 1);
    }
    return Array.from(counts.entries())
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => {
        if (a.count !== b.count)
            return b.count - a.count;
        return a.label.localeCompare(b.label);
    });
}
function rangeOverlapsAny(start, end, ranges) {
    for (const [rStart, rEnd] of ranges) {
        // Half-open ranges: [start, end). Two ranges overlap iff
        // start < rEnd AND rStart < end.
        if (start < rEnd && rStart < end)
            return true;
    }
    return false;
}
/**
 * Marker prefix shared between `redactFlaggedSecrets` (which writes
 * the marker) and `scanForSecrets` callers (which need to detect a
 * marker the high-entropy fallback would otherwise re-flag — see
 * Codex round-3 MAJOR on PR #1075, "idempotence").
 *
 * Exported so future surfaces (e.g. a tool that re-scans a bundle
 * already passed through `redactFlaggedSecrets`) can recognise the
 * marker boundary without inferring the format.
 */
export const REDACTED_FLAG_PREFIX = '[REDACTED-FLAGGED-';
export const REDACTED_FLAG_SUFFIX = ']';
/**
 * Closed-enum map of every `SecretPatternLabel` value. Drives the
 * marker-recognition regex below.
 *
 * `satisfies Record<SecretPatternLabel, true>` forces a compile
 * error if a new pattern label is added to the union but not to
 * this map — same drift-guard pattern as the parity test
 * (`shared/__tests__/lifecycle-event-parity.test.ts`).
 *
 * Codex round-5 HIGH on PR #1075: trusting an arbitrary `[a-z-]+`
 * inside the marker bracket let an attacker hide a token-shaped
 * substring by wrapping it as a fake `[REDACTED-FLAGGED-<long-
 * lowercase-stream>]`. The closed enum cuts that off — only labels
 * the helper itself can emit are honoured.
 */
const SECRET_PATTERN_LABEL_MAP = {
    'github-pat-ghp': true,
    'github-pat-gho': true,
    'github-pat-ghs': true,
    'github-pat-ghu': true,
    'github-pat-ghr': true,
    'github-pat-fine-grained': true,
    'anthropic-key': true,
    'aws-key': true,
    'aws-secret-key': true,
    'datadog-api-key': true,
    'datadog-app-key': true,
    'npm-token': true,
    'http-bearer': true,
    'credentialed-url': true,
    'high-entropy': true,
};
/**
 * Compiled regex matching only `[REDACTED-FLAGGED-<closed-label>]`
 * markers the redaction helper has previously emitted. The label
 * alternation is built from `SECRET_PATTERN_LABEL_MAP` so a future
 * `SecretPatternLabel` addition propagates automatically.
 *
 * Escaping: every label is fixed-text (kebab-case lowercase) so no
 * regex meta-chars appear inside the alternation. We deliberately
 * do NOT use `\w` / `[a-z-]+` here — see the round-5 finding above
 * for why.
 */
const EXISTING_MARKER_RE = new RegExp(`\\[REDACTED-FLAGGED-(?:${Object.keys(SECRET_PATTERN_LABEL_MAP).join('|')})\\]`, 'g');
/**
 * Compute the byte ranges of existing `[REDACTED-FLAGGED-<label>]`
 * markers within a single line, where `<label>` is a known
 * `SecretPatternLabel`. Used by `redactFlaggedSecrets` to skip any
 * flag whose match is FULLY CONTAINED inside an existing marker so
 * re-running the helper on already-redacted text is a no-op.
 */
function existingMarkerRanges(line) {
    const ranges = [];
    EXISTING_MARKER_RE.lastIndex = 0;
    let m;
    while ((m = EXISTING_MARKER_RE.exec(line)) !== null) {
        ranges.push([m.index, m.index + m[0].length]);
        if (m[0].length === 0)
            EXISTING_MARKER_RE.lastIndex++;
    }
    return ranges;
}
/**
 * Run `scanForSecrets` over the input text and replace every flagged
 * byte range with `[REDACTED-FLAGGED-<label>]`. Pure: no I/O, no
 * mutation. Returns the redacted text + the count of replacements.
 *
 * IDEMPOTENT (Codex round-3 MAJOR + round-4 HIGH + round-5 HIGH on
 * PR #1075): the marker string's `REDACTED-FLAGGED-<label>` inner
 * text happens to be 32+ chars of `[A-Za-z0-9-]`, which the high-
 * entropy fallback would otherwise re-flag on a second pass. Before
 * applying replacements, this helper computes the byte ranges of
 * existing markers within each line (matched against the closed
 * `SecretPatternLabel` enum so a fake marker with an arbitrary
 * lowercase label cannot smuggle a secret past) and SKIPS any flag
 * whose match is FULLY CONTAINED inside one. A flag whose match
 * SPANS a marker (e.g. a `credentialed-url` whose URL contains a
 * marker in the middle) IS still wrapped — so the surrounding
 * `user:pass@` userinfo doesn't leak. Running the helper twice on
 * the same input returns count=0 on the second call.
 *
 * Replacement walks ranges within each line right-to-left so leading
 * offsets stay valid as later ones are spliced.
 *
 * Used by:
 *   - The app-menu "Copy Diagnostics to Clipboard" path
 *     (`main/index.ts`), which has no review-UI gate.
 *   - The `app:open-bug-report` IPC surface, ditto.
 *
 * NOT used by the Settings → Diagnostics flow: that surface drives
 * `scanForSecrets()` directly so the user can see + acknowledge the
 * flagged ranges via the `<SecretFlagBanner />` + checkbox gate
 * before copying.
 */
export function redactFlaggedSecrets(text) {
    const flags = scanForSecrets(text);
    if (flags.length === 0)
        return { text, count: 0 };
    const lines = text.split('\n');
    // Bucket flags by line so we can apply them right-to-left, and
    // compute existing-marker ranges per line so we can filter out
    // flags fully contained inside an existing marker (idempotence
    // guard — narrow containment, not naive overlap).
    const byLine = new Map();
    for (const flag of flags) {
        const list = byLine.get(flag.lineIndex);
        if (list)
            list.push(flag);
        else
            byLine.set(flag.lineIndex, [flag]);
    }
    let count = 0;
    for (const [lineIndex, lineFlags] of byLine) {
        const markerRanges = existingMarkerRanges(lines[lineIndex]);
        // Sort descending by start offset so each splice doesn't shift
        // the offsets of the later flags we haven't applied yet.
        lineFlags.sort((a, b) => b.matchedRangeStart - a.matchedRangeStart);
        let line = lines[lineIndex];
        for (const flag of lineFlags) {
            // Skip flags whose match is FULLY CONTAINED inside an existing
            // marker — already redacted, must not re-wrap. Codex round-4
            // HIGH on PR #1075: a wider match (e.g. a `credentialed-url`
            // span that happens to contain an existing marker) MUST still
            // be redacted; we only skip the narrower case where the entire
            // flag sits inside a marker (which is exactly the high-entropy
            // re-flag failure mode).
            //
            // Concrete shape that broke the old "any overlap → skip" rule:
            //   `https://alice:secret[REDACTED-FLAGGED-datadog-api-key]@example.com`
            // The `credentialed-url` match spans the entire URL; the marker
            // is in the middle. Containment check `mStart <= flagStart &&
            // flagEnd <= mEnd` correctly answers "no" → the URL gets wrapped.
            if (markerRanges.some(([mStart, mEnd]) => mStart <= flag.matchedRangeStart && flag.matchedRangeEnd <= mEnd)) {
                continue;
            }
            const marker = `${REDACTED_FLAG_PREFIX}${flag.patternLabel}${REDACTED_FLAG_SUFFIX}`;
            line =
                line.slice(0, flag.matchedRangeStart) +
                    marker +
                    line.slice(flag.matchedRangeEnd);
            count += 1;
        }
        lines[lineIndex] = line;
    }
    return { text: lines.join('\n'), count };
}
//# sourceMappingURL=diagnostics-secret-scan.js.map